import { recoverMessageAddress } from 'viem'

import { Address } from '~/utils/address'

import type { Hex } from 'viem'

export type EoaSignatureCheck = 'valid' | 'signed-as-text' | 'mismatch'

/**
 * Checks a wallet's personal_sign signature over a 32-byte hash against the
 * scheme the smart-vault contracts verify: EIP-191 over the raw bytes.
 *
 * Some wallets (Ledger Live over WalletConnect, the Rainbow extension with a
 * Ledger) re-encode the hash as text before signing. That signature recovers
 * to the right address only when the message is treated as the hex string,
 * which is reported as `signed-as-text` so the UI can name the cause.
 */
export const checkEoaSignature = async ({
  hash,
  signature,
  expectedAddress,
}: {
  hash: Hex
  signature: Hex
  expectedAddress: string
}): Promise<EoaSignatureCheck> => {
  try {
    const rawSigner = await recoverMessageAddress({
      message: { raw: hash },
      signature,
    })
    if (Address.equal(rawSigner, expectedAddress)) return 'valid'

    const textSigner = await recoverMessageAddress({
      message: hash,
      signature,
    })
    if (Address.equal(textSigner, expectedAddress)) return 'signed-as-text'
  } catch {
    // Malformed signature: fall through to mismatch.
  }
  return 'mismatch'
}
